/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import {
    emailOptionalValidationConfig,
    countrySpecificPhoneNumberValidationConfig,
    requiredValueConfig,
    organizationNumberValueConfig,
    registrationNumberValueConfig,
} from '../../../types/ValidationTypes';
import Spinner from '../../atoms/Spinner/Spinner';
import ErrorMessage from '../../atoms/Message/Error/ErrorMessage';
import ModalContainer from '../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../atoms/Modal/HeaderTitle';
import FormRow from '../../atoms/Form/FormRow';
import GridInput from '../../atoms/Input/GridInput';
import FooterButtonContainer from '../../atoms/Modal/FooterButtonContainer';
import { ErrorType } from '../../../types/response/ErrorCodes';
import SuccessMessage from '../../atoms/Message/Success/SuccessMessage';
import Button from '../../atoms/Button/Button';
import { useB2BStore } from '../../../store/b2b/b2bStore';
import { addBuyer } from '../../../api/b2b/b2b';
import MessageContainer from '../../atoms/Message/MessageContainer';
import Popover, { Position } from '../../atoms/Popover/Popover';
import InformationLineIcon from 'remixicon-react/InformationLineIcon';
import Flex from '../../atoms/Box/Flex';
import SelectInput from '../../atoms/Input/SelectInput';
import { useAppState } from '../../../store/appstate';
import { SessionState } from '../../../store/session/sessionSlice';
import { ConnectedStore } from '../../../types/ConnectedStore';
import { BUYER_AUTHORIZATION_INFO } from '../../../constants/LegalTexts';

interface AddBuyerFormProps {
    hide: () => void;
    theme: DefaultTheme;
    initialCountry?: string;
    initialOrgId?: string;
}

type Inputs = {
    organizationNumber: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    registrationNumber: string;
};

const allSupportedCountries = [
    {
        label: 'Sweden',
        value: 'SE',
    },
    {
        label: 'Norway',
        value: 'NO',
    },
];

const AddBuyerForm: React.FC<AddBuyerFormProps> = ({
    hide,
    theme,
    initialCountry,
    initialOrgId: orgId,
}: AddBuyerFormProps) => {
    const storeCountryCodes = (useAppState<ConnectedStore[]>(s => s.session.availableStores) ?? [])
        .filter(x => x.countryCode !== undefined)
        .map(x => x.countryCode as string);

    const storeCountryCodeFrequencies = storeCountryCodes.reduce<Record<string, number>>(
        (acc, code) => {
            acc[code] = (acc[code] || 0) + 1;
            return acc;
        },
        {}
    );

    const sortedCountryCodes = Array.from(new Set(storeCountryCodes)).sort((a, b) => {
        return storeCountryCodeFrequencies[b] - storeCountryCodeFrequencies[a];
    });

    const sortedCountries = sortedCountryCodes
        .map(code => allSupportedCountries.find(c => c.value === code))
        .filter((c): c is { label: string; value: string } => c !== undefined);

    const {
        isAddingBuyer,
        error: addBuyerError,
        isAddBuyerSucceeded,
        addBuyerStarted,
        addBuyerSucceeded,
        addBuyerFailed,
    } = useB2BStore();

    const { selectedOrganization } = useAppState<SessionState>(s => s.session);

    const [popoverOpen, setPopoverOpen] = useState(false);
    const [country, setCountry] = useState(initialCountry || sortedCountries[0].value);

    const form = useForm<Inputs>({
        mode: 'onSubmit',
        shouldUseNativeValidation: true,
        defaultValues: {
            organizationNumber: orgId || '',
        },
    });
    const { getValues, register, handleSubmit } = form;

    const organizationNumber = register('organizationNumber', organizationNumberValueConfig);
    const registrationNumber = register('registrationNumber', registrationNumberValueConfig);
    const firstName = register('firstName', requiredValueConfig);
    const lastName = register('lastName', requiredValueConfig);
    const email = register('email', emailOptionalValidationConfig);
    const phoneNumber = register(
        'phoneNumber',
        countrySpecificPhoneNumberValidationConfig(country)
    );
    const isDisabled = isAddingBuyer;

    const onSubmit = (data: Inputs) => {
        addBuyer(
            data.organizationNumber,
            data.email,
            data.firstName,
            data.lastName,
            data.phoneNumber,
            data.registrationNumber,
            country,
            addBuyerStarted,
            addBuyerSucceeded,
            addBuyerFailed
        );
    };

    return (
        <>
            <ModalContainer position="header">
                <Flex justifyContent="space-between">
                    <HeaderTitle>Add buyer</HeaderTitle>
                    <Popover
                        position={Position.Right}
                        horizontal={Position.Right}
                        item={
                            <InformationLineIcon
                                color={theme.colors.info}
                                size="22"
                                onClick={() => setPopoverOpen(!popoverOpen)}
                                style={{ cursor: 'pointer' }}
                            />
                        }
                        show={popoverOpen}
                        setShow={setPopoverOpen}
                    >
                        <>
                            <strong>👤 Add buyer</strong>
                            <p>
                                Here you can authorize a buyer to make purchases at{' '}
                                {selectedOrganization.name.length > 0 ? (
                                    <strong>{selectedOrganization.name}</strong>
                                ) : (
                                    'your stores'
                                )}
                                .
                            </p>
                            <p>
                                The organization number refers to the buyer&apos;s company, whereas
                                the customer details refer to the individual who will be authorized
                                to make purchases.
                            </p>
                        </>
                    </Popover>
                </Flex>
            </ModalContainer>

            <ModalContainer position="content" noScroll={false}>
                <form onSubmit={handleSubmit(onSubmit)} data-testid="form" id="add-form">
                    <InputContainer>
                        <h3>Company</h3>
                        <FormRow first last>
                            <SelectInput
                                id="country"
                                dataTestId="add-buyer-country-selector"
                                placeholder="Country"
                                onChange={setCountry}
                                value={country}
                                options={sortedCountries}
                                disabled={isDisabled}
                                setHeight={false}
                            />
                            <GridInput
                                type="text"
                                id="organizationNumber"
                                placeholder="Organization number"
                                placeholderRaised={!!getValues(organizationNumber.name)}
                                setRef={organizationNumber.ref}
                                name={organizationNumber.name}
                                onChange={organizationNumber.onChange}
                                disabled={isDisabled}
                                dataTestId="organizationNumber"
                            />
                        </FormRow>
                        <h3>Buyer</h3>
                        <FormRow>
                            <GridInput
                                type="text"
                                id="registrationNumber"
                                placeholder="Registration number"
                                placeholderRaised={!!getValues(registrationNumber.name)}
                                setRef={registrationNumber.ref}
                                name={registrationNumber.name}
                                onChange={registrationNumber.onChange}
                                disabled={isDisabled}
                                dataTestId="registrationNumber"
                            />
                        </FormRow>
                        <FormRow>
                            <GridInput
                                type="text"
                                id="firstname"
                                placeholder="First name"
                                placeholderRaised={!!getValues(firstName.name)}
                                setRef={firstName.ref}
                                name={firstName.name}
                                onChange={firstName.onChange}
                                disabled={isDisabled}
                                dataTestId="firstname"
                            />
                            <GridInput
                                type="text"
                                id="lastname"
                                placeholder="Last name"
                                placeholderRaised={!!getValues(lastName.name)}
                                setRef={lastName.ref}
                                name={lastName.name}
                                onChange={lastName.onChange}
                                disabled={isDisabled}
                                dataTestId="lastname"
                            />
                        </FormRow>

                        <FormRow last>
                            <GridInput
                                type="text"
                                id="phoneNumber"
                                placeholder="Phone number (optional)"
                                placeholderRaised={!!getValues(phoneNumber.name)}
                                setRef={phoneNumber.ref}
                                name={phoneNumber.name}
                                onChange={phoneNumber.onChange}
                                disabled={isDisabled}
                                dataTestId="phoneNumber"
                            />
                            <GridInput
                                type="text"
                                id="email"
                                placeholder="Email address (optional)"
                                placeholderRaised={!!getValues(email.name)}
                                setRef={email.ref}
                                name={email.name}
                                onChange={email.onChange}
                                disabled={isDisabled}
                                dataTestId="email"
                            />
                        </FormRow>
                    </InputContainer>

                    {isAddBuyerSucceeded && (
                        <ResultContainer>
                            <SuccessMessage>Buyer added!</SuccessMessage>
                        </ResultContainer>
                    )}
                </form>
                <OuterMessageContainer>
                    <MessageContainer type="Info" header={'Information on buyer authorization'}>
                        {BUYER_AUTHORIZATION_INFO}
                    </MessageContainer>
                </OuterMessageContainer>
            </ModalContainer>

            {addBuyerError && (
                <ModalContainer position="error">
                    <ErrorMessage error={addBuyerError} errorHeader={ErrorType.B2BAddBuyer} />
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    {!isAddBuyerSucceeded && (
                        <Button onClick={hide} large>
                            Cancel
                        </Button>
                    )}
                    {!isAddBuyerSucceeded && (
                        <Button
                            tabIndex={0}
                            form="add-form"
                            type="submit"
                            large
                            primary
                            disabled={isDisabled}
                            data-testid="add-buyer-submit-button"
                        >
                            {isAddingBuyer ? (
                                <Spinner color={theme.colors.light} size={8} loading />
                            ) : (
                                <span>Add buyer</span>
                            )}
                        </Button>
                    )}
                    {isAddBuyerSucceeded && (
                        <Button onClick={hide} large primary>
                            Close
                        </Button>
                    )}
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

const InputContainer = styled.div`
    display: block;
    padding-bottom: ${props => props.theme.layout.padding.small};
`;

const ResultContainer = styled.div`
    display: flex;
    flex-flow: row;
    justify-content: center;
    padding-bottom: 2.5rem;
`;

const OuterMessageContainer = styled.div`
    width: fit-content;
    height: fit-content;
`;

export default withTheme(AddBuyerForm) as React.ComponentType<Omit<AddBuyerFormProps, 'theme'>>;
